import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  linkWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { phoneAlias } from '../constants/phone';
import { rememberPhone } from '../firebase/phoneIndex';
import { auth, googleProvider } from '../firebase/firebase';
import { createUserDocument, createUserDocumentIfNew, updateUserFullName, getUserDocument } from '../firebase/userService';
import { publishProfileCard } from '../firebase/publicProfileService';
import { markActiveToday } from '../firebase/activityService';

const AuthContext = createContext(null);

// Firebase's User is a class instance, so `{ ...user }` copies its data and
// silently throws away every method on it. React still needs a NEW object
// reference to re-render, so this clones the prototype and property descriptors
// too — new identity, working methods.
function cloneUserWith(user, patch) {
  if (!user) return user;
  const copy = Object.create(
    Object.getPrototypeOf(user),
    Object.getOwnPropertyDescriptors(user),
  );
  return Object.assign(copy, patch);
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // The users/{uid} document — userType, avatarUrl, designer profile. Held here
  // rather than fetched per component so the header, Settings and anywhere else
  // that shows an avatar share ONE read instead of each issuing their own.
  const [profile, setProfile] = useState(null);

  // Keep auth state in sync with Firebase
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Load (and clear) the profile alongside the auth user. `alive` guards against
  // a slow read for a signed-out user landing after someone else has signed in.
  const refreshProfile = useCallback(async () => {
    if (!auth.currentUser) { setProfile(null); return null; }
    try {
      const doc = await getUserDocument(auth.currentUser.uid);
      setProfile(doc);
      return doc;
    } catch {
      return null;                        // an unreadable profile must not break auth
    }
  }, []);

  useEffect(() => {
    let alive = true;
    if (!user?.uid) { setProfile(null); return undefined; }

    // "Active today", for the admin dashboard. Here because this is the one
    // effect that runs exactly once per signed-in session, and it costs at
    // most one write per user per day — see activityService. Deliberately not
    // awaited: nothing on screen depends on it.
    markActiveToday(user.uid);

    (async () => {
      const doc = await getUserDocument(user.uid).catch(() => null);
      if (alive) setProfile(doc);

      // ── BACKFILL THE PUBLIC CARD ──────────────────────────────────────────
      // Every profile save publishes one from now on, but accounts that already
      // existed have none — and a customer with no card shows the default icon
      // in every comment they have ever written.
      //
      // NO READ: checking whether the card is there would cost a read on every
      // sign-in for the life of the app, to answer a question that changes
      // almost never. Instead a local marker records WHAT was last published —
      // so a name or picture changed on another device republishes here, and a
      // write that failed is retried rather than remembered as done.
      const stamp = `${(doc?.fullName || '').length}:${(doc?.avatarUrl || '').length}`;
      const key = `tunify.card.v1.${user.uid}`;
      if (doc && localStorage.getItem(key) !== stamp) {
        publishProfileCard(user.uid, { name: doc.fullName || '', avatarUrl: doc.avatarUrl || '' })
          .then((ok) => {
            if (!ok) return;               // failed — try again next time
            try { localStorage.setItem(key, stamp); } catch { /* private mode */ }
          });
      }
    })();
    return () => { alive = false; };
  }, [user?.uid]);

  // `userType` / `roleProfile` come from the signup type step. Both are optional
  // so any existing caller that passes only the first three arguments keeps
  // creating a customer account exactly as before.
  //
  // `roleProfile` is whichever profile the chosen role publishes — a designer's
  // or a shop's. It is passed through untouched; createUserDocument is the one
  // place that knows how to serialize each shape.
  const register = useCallback(async (email, password, displayName, userType, roleProfile, avatarUrl) => {
    // 1. Create the Firebase Auth user
    const { user } = await createUserWithEmailAndPassword(auth, email, password);

    try {
      // 2. Run Auth profile update + Firestore document creation in parallel
      await Promise.all([
        updateProfile(user, { displayName: displayName.trim() }),
        createUserDocument(user, displayName, userType, roleProfile, avatarUrl),
      ]);
    } catch (err) {
      // 3. Rollback — ONLY for a failure that leaves the account unusable.
      //
      // This used to delete the account on ANY error here, which turned a
      // recoverable hiccup into a destroyed registration: the user was told
      // "account created", bounced to /login a second later, and then couldn't
      // sign in because the account no longer existed. Any error whose blast
      // radius is smaller than the whole account must NOT reach this line —
      // see createUserDocument, where the non-essential writes are best-effort.
      try {
        await user.delete();
      } catch {
        // Delete can itself fail (stale token, network). Swallow it so the
        // original error is what surfaces — and note that this is exactly how
        // orphaned Auth accounts with no Firestore document get created.
      }
      throw err;
    }

    // 4. Push the updated user into local state so displayName is immediately
    //    available without waiting for the next onAuthStateChanged tick.
    //    Cloned WITH its prototype: a plain { ...user } spread drops every
    //    method on the Firebase User (getIdToken, reload, delete), so anything
    //    downstream calling them would fail on a freshly registered user.
    setUser(cloneUserWith(user, { displayName: displayName.trim() }));
    return user;
  }, []);

  const login = useCallback((email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  }, []);

  // Returns `isNewAccount` alongside the user: OAuth gives no sign-up step, so
  // this is the only moment anything knows the difference between someone
  // joining and someone coming back — and the two need different destinations
  // (a new account has not been asked customer-or-designer yet).
  const loginWithGoogle = useCallback(async () => {
    const { user } = await signInWithPopup(auth, googleProvider);
    const isNewAccount = await createUserDocumentIfNew(user);
    return { user, isNewAccount };
  }, []);

  // ── PHONE ─────────────────────────────────────────────────────────────────
  // The second half of phone sign-in: the SMS has been sent and the code typed.
  // Sending lives in firebase/phoneAuth, because it owns a reCAPTCHA widget and
  // a DOM node and has no business in a context provider; confirming lives here
  // for the same reason the Google flow does — it is the moment an account
  // comes into existence, and users/{uid} has to be created with it.
  //
  // SHAPED LIKE loginWithGoogle ON PURPOSE. Both are one-step sign-ins with no
  // registration form behind them, so neither has been asked customer-or-
  // designer. Returning `isNewAccount` is what lets /login, /register and the
  // guest modal each route a new account to /account-type in their own way,
  // exactly as they already do for Google.
  //
  // `confirmationResult` comes from sendVerificationCode and is held by the
  // caller: it is a live handle to one verification attempt, and it is the only
  // thing that can turn a six-digit code into a signed-in user.
  const loginWithPhone = useCallback(async (confirmationResult, code) => {
    const { user } = await confirmationResult.confirm(String(code || '').trim());
    // NOT AWAITED FOR ITS VALUE ALONE: this is also what gives a brand-new
    // phone account a users/{uid} document at all. Without it the account
    // exists in Auth and nowhere else, and every profile read returns null.
    const isNewAccount = await createUserDocumentIfNew(user);
    // NOT AWAITED, and after the line that matters. Records the number in
    // `phoneIndex` so the sign-up form can refuse a duplicate BEFORE paying for
    // an SMS — see firebase/phoneIndex.js. Written on every phone sign-in
    // rather than only the one that creates the account, which is what
    // backfills numbers registered before the index existed. A failure must
    // never surface here: the sign-in has already succeeded.
    rememberPhone(user.phoneNumber);
    return { user, isNewAccount };
  }, []);

  // ── PHONE + PASSWORD ──────────────────────────────────────────────────────
  // The other door into a phone account. Firebase has no phone-and-password
  // credential, so the number carries an email-shaped ALIAS and this is an
  // ordinary email/password sign-in against it — Firebase stores and checks the
  // password, exactly as for anybody else. See constants/phone.js.
  //
  // The alias is derived from the number every time and never stored anywhere
  // the reader can see or type, so the two can never drift apart.
  const loginWithPhonePassword = useCallback((e164, password) => {
    const alias = phoneAlias(e164);
    if (!alias) throw new Error('invalid-phone');
    return signInWithEmailAndPassword(auth, alias, password);
  }, []);

  /**
   * Gives the CURRENT account a password it can be reached by.
   *
   * Called straight after a phone sign-up, while the OTP has just proved the
   * number — which is what makes it safe: linking a credential requires being
   * signed in already, so this can only ever add a password to an account the
   * caller has just demonstrated they control.
   *
   * `linkWithCredential`, NOT createUser: the account already exists and has a
   * uid that photos, chats and likes are keyed on. Linking adds a second way
   * into that same account; creating would make a second, empty one.
   */
  const setPhonePassword = useCallback(async (password) => {
    const current = auth.currentUser;
    if (!current) throw new Error('not-signed-in');
    const e164 = current.phoneNumber;
    if (!e164) throw new Error('no-phone-on-account');

    const credential = EmailAuthProvider.credential(phoneAlias(e164), password);
    await linkWithCredential(current, credential);
    // The linked identifier lands on the user record, so the local copy has to
    // be replaced or `user.email` keeps reading null until the next reload —
    // cloned WITH its prototype for the reason described at cloneUserWith.
    setUser(u => cloneUserWith(u, { email: phoneAlias(e164) }));
  }, []);

  /** Does this account already have a password? Decides whether the sign-in
   *  form can offer one, and whether Settings offers "set" or "change". */
  const hasPasswordCredential = useCallback(() => {
    return (auth.currentUser?.providerData || [])
      .some(p => p.providerId === 'password');
  }, []);

  const logout = useCallback(() => {
    return signOut(auth);
  }, []);

  const resetPassword = useCallback((email) => {
    return sendPasswordResetEmail(auth, email);
  }, []);

  // Settings page: change the account's full name. Updates both Firebase Auth
  // (the `displayName` shown everywhere via useAuth().user) and the Firestore
  // users/{uid} document (the `fullName` field workspace/sharing code reads).
  const updateFullName = useCallback(async (fullName) => {
    const name = fullName.trim();
    if (!auth.currentUser) throw new Error('Not signed in');
    await updateProfile(auth.currentUser, { displayName: name });
    await updateUserFullName(auth.currentUser.uid, name);
    // updateProfile mutates auth.currentUser in place but does NOT fire
    // onAuthStateChanged, so nudge local state directly (same pattern as
    // register() above) — otherwise every displayName read from useAuth().user
    // (header avatar, dashboard chip) would keep showing the old name until
    // the next full page reload.
    setUser((u) => cloneUserWith(u, { displayName: name }));
  }, []);

  // Pushes a name into Firebase AUTH only — for callers that have already
  // written Firestore themselves (the designer profile save writes fullName as
  // part of a larger document, so routing it through updateFullName would
  // duplicate that write).
  //
  // Without this, saving a designer profile updated Firestore but left
  // auth.currentUser.displayName untouched, and every header that reads
  // displayName kept showing the old name until the next full sign-in.
  const syncAuthName = useCallback(async (fullName) => {
    const name = String(fullName || '').trim();
    if (!auth.currentUser || !name || auth.currentUser.displayName === name) return;
    await updateProfile(auth.currentUser, { displayName: name });
    setUser((u) => cloneUserWith(u, { displayName: name }));
  }, []);

  // Settings page: change the account's password while already signed in.
  // Firebase requires a "recent" sign-in for this — reauthenticating with the
  // CURRENT password both proves the user really is the account owner (this
  // IS the identity check here, unlike the forgot-password flow's email code)
  // and refreshes that recency, so the updatePassword call right after it
  // never hits 'auth/requires-recent-login'.
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    if (!auth.currentUser?.email) throw new Error('Not signed in');
    const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, credential);
    await updatePassword(auth.currentUser, newPassword);
  }, []);

  const value = {
    user,
    loading,
    profile,          // users/{uid}: userType, avatarUrl, designer
    refreshProfile,   // call after saving the profile so the header updates
    syncAuthName,     // push a name into Firebase Auth (Firestore written by caller)
    login,
    register,
    loginWithGoogle,
    loginWithPhone,        // confirm an SMS code → { user, isNewAccount }
    loginWithPhonePassword, // phone number + password, via the alias
    setPhonePassword,      // link a password to a phone account
    hasPasswordCredential,
    logout,
    resetPassword,
    updateFullName,
    changePassword,
  };

  // Don't render children until the initial auth check is done
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
